import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Autocomplete, Box, Button, Card, Chip, CircularProgress, TextField, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { useIsMutating, useMutation, useMutationState, useQueryClient } from "@tanstack/react-query"
import { omit } from "lodash"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { log } from "helpers/logger"
import { useAccountAlias, useUserId } from "resources/account"
import type { ShareWorkflowDraft } from "resources/scheduled-workflows/draft-contract"
import { isScheduledWorkflowNotFound } from "resources/scheduled-workflows/errors"
import { useScheduledWorkflow, useShareScheduledWorkflow } from "resources/scheduled-workflows/hooks"
import { SHARE_ARCHIVED_COPY, shareErrorMessage } from "../scheduled-workflows/activate-error"
import { ACTIONS_SX, CARD_SX, HEAD_SX, NAME_SX } from "./card-styles"
import { NO_NOTIFICATION, WORKFLOW_GONE } from "./create-draft-failure"
import {
  type DirectoryEntry,
  SHARE_QUERY_KEYS,
  useFetchFreshShareState,
  useGrantShareAccess,
  useRevokeShareAccess,
  useScheduledWorkflowShareResource,
  useShareDirectory,
  useShareRecipients,
} from "./resource-sharing"
import { recipientAnalyticsFields } from "./share-dialog"
import { entryKey } from "./share-entry-key"
import {
  type AddRowStatus,
  type ChooseReason,
  type RemoveRowStatus,
  type RowDecision,
  type ShareDraftPlan,
  canApply,
  isAlreadyApplied,
  isSameChange,
  planShareDraft,
  visibleText,
} from "./share-draft-resolution"

const RESOURCE_TYPE = "SCHEDULED_WORKFLOW"
const SHARE_SOURCE = "kelly_card"
const ACCESS_NOTE = "Everyone you add can see this workflow's reports. They can't edit or re-share it."
const CHANGED_COPY = "Who can see this workflow changed since this card loaded. Check the names again, then confirm."
const RETRY_COPY = "Couldn't check who can see this workflow. Try again."
const LOAD_COPY = "Couldn't load the people in this account. Try again later."
const READ_ERROR_COPY = "Couldn't load this workflow. Try again."
const APPLIED_COPY = "This workflow is already shared this way."
const NOTHING_TO_REMOVE_COPY = "No one listed has access to this workflow, so there is nothing to remove."

const CHOOSE_NOTES: Record<"add" | "remove", Record<ChooseReason, string>> = {
  add: {
    ambiguous: "More than one person or team matches this name.",
    unknown: "No one in this account has this exact name or email.",
    hidden: "This name contained hidden characters, so it wasn't matched.",
  },
  remove: {
    ambiguous: "More than one person or team with access matches this name.",
    unknown: "No one with access has this exact name or email.",
    hidden: "This name contained hidden characters, so it wasn't matched.",
  },
}

const SECTION_SX = { display: "flex", flexDirection: "column", gap: 1 } satisfies SxProps<Theme>
const SECTION_LABEL_SX = { fontWeight: 600 } satisfies SxProps<Theme>
const LIST_SX = { display: "flex", flexDirection: "column", gap: 1, listStyle: "none", m: 0, p: 0 } satisfies SxProps<Theme>
const ROW_SX = { display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 } satisfies SxProps<Theme>
const ROW_TEXT_SX = { display: "flex", flexDirection: "column", minWidth: 0, flex: "1 1 12rem" } satisfies SxProps<Theme>
const PICKER_SX = { flex: "1 1 14rem", minWidth: "12rem" } satisfies SxProps<Theme>

type Side = "add" | "remove"

type ConfirmFailure = { kind: "route", message: string } | { kind: "changed" } | { kind: "retry" }

interface Outcome {
  granted: DirectoryEntry[]
  removed: DirectoryEntry[]
  failed: DirectoryEntry[]
}

type ConfirmResult = { kind: "applied", outcome: Outcome } | { kind: "failed", failure: ConfirmFailure }

type ConfirmState = ConfirmResult | "errored" | undefined

type AppliedResult = Extract<ConfirmResult, { kind: "applied" }>

type OnDecide = (rowKey: string, decision: RowDecision) => void

export interface ShareDraftCardProps {
  draft: ShareWorkflowDraft
}

function entryLabel(entry: DirectoryEntry): string {
  return visibleText(entry.name)
}

function optionLabel(entry: DirectoryEntry): string {
  if (entry.kind === "team") return `${entryLabel(entry)} (team)`
  return entry.email == null ? entryLabel(entry) : `${entryLabel(entry)} (${visibleText(entry.email)})`
}

function namesOf(entries: DirectoryEntry[]): string {
  return entries.map(entryLabel).join(", ")
}

function isApplied(result: ConfirmState): result is AppliedResult {
  return result !== undefined && result !== "errored" && result.kind === "applied"
}

function uniqueEntries(entries: DirectoryEntry[]): DirectoryEntry[] {
  return [...new Map(entries.map(entry => [entryKey(entry), entry])).values()]
}

function combinedOutcome(results: ConfirmState[]): Outcome | null {
  const applied = results.filter(isApplied)
  const last = results.at(-1)
  const granted = uniqueEntries(applied.flatMap(result => result.outcome.granted))
  const removed = uniqueEntries(applied.flatMap(result => result.outcome.removed))
  if (isApplied(last)) return { granted, removed, failed: last.outcome.failed }
  if (granted.length === 0 && removed.length === 0) return null
  return { granted, removed, failed: [] }
}

function trackQuietly(track: () => void) {
  try {
    track()
  } catch (error) {
    log(error)
  }
}

function useFocusWhen<T extends HTMLElement>(shouldFocus: boolean) {
  const ref = useRef<T>(null)
  useEffect(() => {
    if (shouldFocus) ref.current?.focus()
  }, [shouldFocus])
  return ref
}

function EntryText({ entry, side }: { entry: DirectoryEntry, side: Side }) {
  const teamNote = side === "add" ? "Team: everyone on it gets access" : "Team: everyone on it loses access"
  return (
    <Box sx={ROW_TEXT_SX}>
      <Typography variant="body2" sx={NAME_SX} data-testid="kelly-share-draft-entry-name">{entryLabel(entry)}</Typography>
      <Typography variant="caption" color="text.secondary" data-testid="kelly-share-draft-entry-detail">
        {entry.kind === "team" ? teamNote : visibleText(entry.email ?? "")}
      </Typography>
    </Box>
  )
}

function SaidText({ said, note }: { said: string, note: string }) {
  return (
    <Box sx={ROW_TEXT_SX}>
      <Typography variant="body2" sx={NAME_SX} data-testid="kelly-share-draft-said">{`"${visibleText(said)}"`}</Typography>
      <Typography variant="caption" color="text.secondary" data-testid="kelly-share-draft-note">{note}</Typography>
    </Box>
  )
}

interface RowControls {
  rowKey: string
  isDisabled: boolean
  isFocused: boolean
  onDecide: OnDecide
}

function NamePicker({ rowKey, said, note, options, isDisabled, isFocused, onDecide }: RowControls & { said: string, note: string, options: DirectoryEntry[] }) {
  const inputRef = useFocusWhen<HTMLInputElement>(isFocused)
  const onPick = useCallback((_event: unknown, entry: DirectoryEntry | null) => {
    if (entry != null) onDecide(rowKey, { kind: "pick", entry })
  }, [onDecide, rowKey])
  const onSkip = useCallback(() => onDecide(rowKey, { kind: "skip" }), [onDecide, rowKey])

  return (
    <Box component="li" sx={ROW_SX} data-testid="kelly-share-draft-row">
      <SaidText said={said} note={note} />
      <Autocomplete
        size="small"
        sx={PICKER_SX}
        options={options}
        value={null}
        disabled={isDisabled}
        onChange={onPick}
        getOptionLabel={optionLabel}
        isOptionEqualToValue={(option, value) => entryKey(option) === entryKey(value)}
        renderOption={(props, entry) => <li key={entryKey(entry)} {...omit(props, "key")}>{optionLabel(entry)}</li>}
        renderInput={params => <TextField {...params} inputRef={inputRef} label={`Choose for "${visibleText(said)}"`} />}
        data-testid="kelly-share-draft-picker"
      />
      <Button size="small" disabled={isDisabled} onClick={onSkip} aria-label={`Skip "${visibleText(said)}"`} data-testid="kelly-share-draft-skip">Skip</Button>
    </Box>
  )
}

function SettledRow({ rowKey, entry, side, label, color, isPicked, canSkip, isDisabled, isFocused, onDecide }: RowControls & {
  entry: DirectoryEntry
  side: Side
  label: string
  color: "primary" | "warning" | "error" | "default"
  isPicked: boolean
  canSkip: boolean
}) {
  const buttonRef = useFocusWhen<HTMLButtonElement>(isFocused)
  const onChange = useCallback(() => onDecide(rowKey, { kind: "undecided" }), [onDecide, rowKey])
  const onSkip = useCallback(() => onDecide(rowKey, { kind: "skip" }), [onDecide, rowKey])
  return (
    <Box component="li" sx={ROW_SX} data-testid="kelly-share-draft-row">
      <EntryText entry={entry} side={side} />
      <Chip size="small" variant="outlined" color={color} label={label} data-testid="kelly-share-draft-row-status" />
      {isPicked && <Button ref={buttonRef} size="small" disabled={isDisabled} onClick={onChange} aria-label={`Change ${entryLabel(entry)}`}>Change</Button>}
      {canSkip && <Button ref={isPicked ? undefined : buttonRef} size="small" disabled={isDisabled} onClick={onSkip} aria-label={`Skip ${entryLabel(entry)}`} data-testid="kelly-share-draft-skip">Skip</Button>}
    </Box>
  )
}

function SkippedRow({ rowKey, said, isDisabled, isFocused, onDecide }: RowControls & { said: string }) {
  const buttonRef = useFocusWhen<HTMLButtonElement>(isFocused)
  const onUndo = useCallback(() => onDecide(rowKey, { kind: "undecided" }), [onDecide, rowKey])
  return (
    <Box component="li" sx={ROW_SX} data-testid="kelly-share-draft-row">
      <SaidText said={said} note="You skipped this name." />
      <Chip size="small" label="Skipped" data-testid="kelly-share-draft-row-status" />
      <Button ref={buttonRef} size="small" disabled={isDisabled} onClick={onUndo} aria-label={`Undo skip of "${visibleText(said)}"`}>Undo</Button>
    </Box>
  )
}

function NoteRow({ said, label }: { said: string, label: string }) {
  return (
    <Box component="li" sx={ROW_SX} data-testid="kelly-share-draft-row">
      <SaidText said={said} note="Nothing to change." />
      <Chip size="small" variant="outlined" label={label} data-testid="kelly-share-draft-row-status" />
    </Box>
  )
}

interface RowProps<S> extends RowControls {
  said: string
  status: S
  isPicked: boolean
}

function AddRow({ said, status, isPicked, ...controls }: RowProps<AddRowStatus>) {
  if (status.kind === "skipped") return <SkippedRow said={said} {...controls} />
  if (status.kind === "choose") return <NamePicker said={said} note={CHOOSE_NOTES.add[status.reason]} options={status.options} {...controls} />
  if (status.kind === "conflict") return <SettledRow entry={status.entry} side="add" label="Also listed to remove" color="error" isPicked={isPicked} canSkip {...controls} />
  if (status.kind === "hasAccess") return <SettledRow entry={status.entry} side="add" label="Already has access" color="default" isPicked={isPicked} canSkip={false} {...controls} />
  return <SettledRow entry={status.entry} side="add" label="Gets access" color="primary" isPicked={isPicked} canSkip={false} {...controls} />
}

function RemoveRow({ said, status, isPicked, ...controls }: RowProps<RemoveRowStatus>) {
  if (status.kind === "skipped") return <SkippedRow said={said} {...controls} />
  if (status.kind === "choose") return <NamePicker said={said} note={CHOOSE_NOTES.remove[status.reason]} options={status.options} {...controls} />
  if (status.kind === "conflict") return <SettledRow entry={status.entry} side="remove" label="Also listed to add" color="error" isPicked={isPicked} canSkip {...controls} />
  if (status.kind === "owner") return <NoteRow said={said} label="Owns this workflow" />
  if (status.kind === "notShared") return <NoteRow said={said} label="Doesn't have access" />
  return <SettledRow entry={status.entry} side="remove" label="Loses access" color="warning" isPicked={isPicked} canSkip={false} {...controls} />
}

function OutcomeAlert({ outcome, isDismissed }: { outcome: Outcome, isDismissed: boolean }) {
  const lines = [
    outcome.granted.length > 0 ? `Shared with ${namesOf(outcome.granted)}.` : null,
    outcome.removed.length > 0 ? `Removed ${namesOf(outcome.removed)}.` : null,
  ].filter((line): line is string => line !== null)
  if (outcome.failed.length > 0) {
    return (
      <Alert severity="error" data-testid="kelly-workflow-draft-error">
        {[...lines, `Couldn't change access for ${namesOf(outcome.failed)}.${isDismissed ? "" : " Try again."}`].join(" ")}
      </Alert>
    )
  }
  return <Alert severity="success" data-testid="kelly-workflow-draft-done">{lines.join(" ")}</Alert>
}

function failureCopy(failure: ConfirmFailure): string {
  if (failure.kind === "route") return failure.message
  if (failure.kind === "changed") return CHANGED_COPY
  return RETRY_COPY
}

export function shareDraftConfirmKey(accountAlias: string, draftId: string) {
  return ["kelly", accountAlias, "share-draft-confirm", draftId] as const
}

export function ShareDraftCard({ draft }: ShareDraftCardProps) {
  const analytics = useAnalytics()
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const queryClient = useQueryClient()
  const { scheduledWorkflowId } = draft

  const { data: workflow, error: readError, refetch: refetchWorkflow } = useScheduledWorkflow(scheduledWorkflowId)
  const isUsable = workflow != null && workflow.archivedAt == null
  const shareResource = useScheduledWorkflowShareResource(scheduledWorkflowId, isUsable)
  const { directory, isLoading: isDirectoryLoading, isError: isDirectoryErrored } = useShareDirectory(isUsable)
  const { recipients, isLoading: isRecipientsLoading, isError: isRecipientsErrored } = useShareRecipients(shareResource.resourceId, RESOURCE_TYPE)

  const shareRoute = useShareScheduledWorkflow(NO_NOTIFICATION)
  const grantAccess = useGrantShareAccess(RESOURCE_TYPE)
  const revokeAccess = useRevokeShareAccess(RESOURCE_TYPE)
  const fetchFreshShareState = useFetchFreshShareState(RESOURCE_TYPE)

  const confirmKey = useMemo(() => shareDraftConfirmKey(accountAlias, draft.draftId), [accountAlias, draft.draftId])
  const isApplying = useIsMutating({ mutationKey: confirmKey }) > 0
  const results = useMutationState({
    filters: { mutationKey: confirmKey },
    select: (mutation): ConfirmState => (mutation.state.status === "error" ? "errored" : mutation.state.data as ConfirmResult | undefined),
  })
  const lastResult = results.at(-1)
  const outcome = combinedOutcome(results)
  let failure: ConfirmFailure | null = null
  if (lastResult === "errored") failure = { kind: "retry" }
  else if (lastResult?.kind === "failed") failure = lastResult.failure

  const [decisions, setDecisions] = useState<Partial<Record<string, RowDecision>>>({})
  const [focusedRowKey, setFocusedRowKey] = useState<string | null>(null)
  const [isDismissed, setIsDismissed] = useState(false)
  const [isVerified, setIsVerified] = useState(false)

  const plan = useMemo(
    () => planShareDraft({ add: draft.add, remove: draft.remove, directory, recipients, userId, decisions }),
    [draft.add, draft.remove, directory, recipients, userId, decisions],
  )

  const name = workflow == null ? "Workflow" : visibleText(workflow.name)
  const isMissing = isScheduledWorkflowNotFound(readError)
  const isReadErrored = readError != null && !isMissing && workflow == null
  const isArchived = workflow?.archivedAt != null
  const isUnavailable = isMissing || isArchived
  const isLoadErrored = shareResource.isError || isDirectoryErrored || isRecipientsErrored
  const isReady = isUsable && !shareResource.isLoading && !isDirectoryLoading && !isRecipientsLoading && !isLoadErrored
  const hasApplied = outcome !== null && (outcome.granted.length > 0 || outcome.removed.length > 0)
  const isSucceeded = isApplied(lastResult) && lastResult.outcome.failed.length === 0
  const looksApplied = isReady && outcome === null && isAlreadyApplied(plan)
  const showsApplied = looksApplied && isVerified
  const showsRows = (isReady || isApplying) && !isDismissed && !isSucceeded && !showsApplied
  const canConfirm = isReady && !isApplying && canApply(plan)

  useEffect(() => {
    if (!looksApplied || isVerified) return undefined
    let isCurrent = true
    const resourceId = shareResource.resourceId
    const refetchOptions = { throwOnError: true }
    void Promise.all([
      queryClient.refetchQueries({ queryKey: SHARE_QUERY_KEYS.workflowShares(accountAlias), type: "active" }, refetchOptions),
      queryClient.refetchQueries({ queryKey: SHARE_QUERY_KEYS.directory(accountAlias), type: "active" }, refetchOptions),
      resourceId === null ? undefined : queryClient.refetchQueries({ queryKey: SHARE_QUERY_KEYS.recipients(accountAlias, RESOURCE_TYPE, resourceId), type: "active" }, refetchOptions),
    ]).then(() => {
      if (isCurrent) setIsVerified(true)
    }, log)
    return () => { isCurrent = false }
  }, [accountAlias, isVerified, looksApplied, queryClient, shareResource.resourceId])

  const onDecide = useCallback((rowKey: string, decision: RowDecision) => {
    setDecisions(previous => ({ ...previous, [rowKey]: decision }))
    setFocusedRowKey(rowKey)
  }, [])

  const onRetryRead = useCallback(() => { void refetchWorkflow() }, [refetchWorkflow])

  const onDismiss = useCallback(() => setIsDismissed(true), [])

  const applyPlan = useCallback(async (resourceId: string, confirmed: ShareDraftPlan): Promise<Outcome> => {
    const failed: DirectoryEntry[] = []
    let granted: DirectoryEntry[] = []
    if (confirmed.grants.length > 0) {
      try {
        const refused = await grantAccess.mutateAsync({ resourceId, recipients: confirmed.grants })
        const refusedKeys = new Set(refused.map(entryKey))
        granted = confirmed.grants.filter(entry => !refusedKeys.has(entryKey(entry)))
        failed.push(...refused)
      } catch (error) {
        log(error)
        failed.push(...confirmed.grants)
      }
    }
    if (granted.length > 0) {
      trackQuietly(() => analytics.track(AI_TEAMMATE_EVENTS.SCHEDULED_WORKFLOW_SHARED, { scheduledWorkflowId, source: SHARE_SOURCE, ...recipientAnalyticsFields(granted) }))
    }
    const removed: DirectoryEntry[] = []
    for (const recipient of confirmed.revokes) {
      try {
        await revokeAccess.mutateAsync({ resourceId, recipient })
      } catch (error) {
        log(error)
        failed.push(recipient)
        continue
      }
      removed.push(recipient)
      trackQuietly(() => analytics.track(AI_TEAMMATE_EVENTS.SCHEDULED_WORKFLOW_UNSHARED, { scheduledWorkflowId, source: SHARE_SOURCE, recipientKind: recipient.kind, recipientId: recipient.id }))
    }
    return { granted, removed, failed }
  }, [analytics, grantAccess, revokeAccess, scheduledWorkflowId])

  const runConfirm = useCallback(async (): Promise<ConfirmResult> => {
    let resourceId: string
    try {
      resourceId = (await shareRoute.mutateAsync(scheduledWorkflowId)).resourceId
    } catch (error) {
      return { kind: "failed", failure: { kind: "route", message: shareErrorMessage(error) } }
    }
    try {
      let confirmed: ShareDraftPlan
      try {
        const fresh = await fetchFreshShareState(resourceId)
        confirmed = planShareDraft({ add: draft.add, remove: draft.remove, directory: fresh.directory, recipients: fresh.recipients, userId, decisions })
      } catch (error) {
        log(error)
        return { kind: "failed", failure: { kind: "retry" } }
      }
      if (!isSameChange(confirmed, plan)) return { kind: "failed", failure: { kind: "changed" } }
      return { kind: "applied", outcome: await applyPlan(resourceId, confirmed) }
    } finally {
      void queryClient.invalidateQueries({ queryKey: SHARE_QUERY_KEYS.workflowShares(accountAlias) })
      void queryClient.invalidateQueries({ queryKey: SHARE_QUERY_KEYS.recipients(accountAlias, RESOURCE_TYPE, resourceId) })
    }
  }, [accountAlias, applyPlan, decisions, draft.add, draft.remove, fetchFreshShareState, plan, queryClient, scheduledWorkflowId, shareRoute, userId])

  const confirm = useMutation({ mutationKey: confirmKey, mutationFn: runConfirm, onError: log })

  const onConfirmClick = useCallback(() => {
    if (queryClient.isMutating({ mutationKey: confirmKey }) > 0) return
    confirm.mutate(undefined)
  }, [confirm, confirmKey, queryClient])

  const rowControls = (rowKey: string) => ({ rowKey, isDisabled: isApplying, isFocused: focusedRowKey === rowKey, onDecide })

  return (
    <Card variant="outlined" sx={CARD_SX} data-testid="kelly-workflow-draft">
      <Box sx={HEAD_SX}>
        <Typography sx={NAME_SX} data-testid="kelly-workflow-draft-name">{name}</Typography>
        <Chip size="small" variant="outlined" color="primary" label="Shares a workflow" data-testid="kelly-workflow-draft-action-chip" />
      </Box>
      <Typography variant="body2" color="text.secondary">{ACCESS_NOTE}</Typography>
      {isMissing && <Alert severity="warning" data-testid="kelly-workflow-draft-id-not-found">{WORKFLOW_GONE}</Alert>}
      {!isMissing && isArchived && <Alert severity="warning" data-testid="kelly-workflow-draft-archived">{SHARE_ARCHIVED_COPY}</Alert>}
      {isReadErrored && (
        <Alert severity="error" data-testid="kelly-workflow-draft-read-error" action={<Button color="inherit" size="small" onClick={onRetryRead} data-testid="kelly-workflow-draft-read-retry">Retry</Button>}>
          {READ_ERROR_COPY}
        </Alert>
      )}
      {isUsable && isLoadErrored && <Alert severity="error" data-testid="kelly-workflow-draft-load-error">{LOAD_COPY}</Alert>}
      {showsRows && plan.adds.length > 0 && (
        <Box sx={SECTION_SX} data-testid="kelly-share-draft-adds">
          <Typography variant="body2" sx={SECTION_LABEL_SX} id={`${draft.draftId}-adds`}>Give access</Typography>
          <Box component="ul" sx={LIST_SX} aria-labelledby={`${draft.draftId}-adds`}>
            {plan.adds.map(row => <AddRow key={row.key} said={row.said} status={row.status} isPicked={row.isPicked} {...rowControls(row.key)} />)}
          </Box>
        </Box>
      )}
      {showsRows && plan.removes.length > 0 && (
        <Box sx={SECTION_SX} data-testid="kelly-share-draft-removes">
          <Typography variant="body2" sx={SECTION_LABEL_SX} id={`${draft.draftId}-removes`}>Remove access</Typography>
          <Box component="ul" sx={LIST_SX} aria-labelledby={`${draft.draftId}-removes`}>
            {plan.removes.map(row => <RemoveRow key={row.key} said={row.said} status={row.status} isPicked={row.isPicked} {...rowControls(row.key)} />)}
          </Box>
        </Box>
      )}
      {((isUsable && !isLoadErrored && !isReady && !isApplying) || (workflow == null && readError == null)) && <CircularProgress size={18} aria-label="Loading who can see this workflow" data-testid="kelly-share-draft-loading" />}
      {outcome !== null && <OutcomeAlert outcome={outcome} isDismissed={isDismissed} />}
      {failure !== null && !isUnavailable && !isDismissed && <Alert severity="error" data-testid="kelly-workflow-draft-error">{failureCopy(failure)}</Alert>}
      {isDismissed && !hasApplied && <Alert severity="info" data-testid="kelly-workflow-draft-dismissed">Dismissed. No access was changed.</Alert>}
      {showsApplied && !isDismissed && <Alert severity="info" data-testid="kelly-workflow-draft-applied">{plan.adds.length === 0 ? NOTHING_TO_REMOVE_COPY : APPLIED_COPY}</Alert>}
      {showsRows && (
        <Box sx={ACTIONS_SX}>
          {looksApplied && !isVerified && <Typography variant="caption" color="text.secondary" data-testid="kelly-share-draft-checking">Checking who has access…</Typography>}
          {plan.undecided > 0 && <Typography variant="caption" color="text.secondary">Choose or skip each name above.</Typography>}
          {plan.conflicts > 0 && <Typography variant="caption" color="text.secondary">Someone is listed to add and to remove. Skip one of them.</Typography>}
          <Button
            variant="contained"
            size="small"
            disabled={!canConfirm}
            onClick={onConfirmClick}
            data-testid="kelly-workflow-draft-confirm"
            startIcon={isApplying ? <CircularProgress size={14} aria-label="Applying" /> : undefined}
          >
            Confirm sharing
          </Button>
          <Button size="small" disabled={isApplying} onClick={onDismiss} data-testid="kelly-share-draft-dismiss">Dismiss</Button>
        </Box>
      )}
    </Card>
  )
}
